"use client";

import React from "react";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { ArrowRight } from "lucide-react";
interface ServiceItem {
  title: string;
  to: string;
  img: string;
  alt: string;
  description: string;
}
const roofingServices: ServiceItem[] = [{
  title: "Roofing Services",
  to: "/RoofingServicesPage",
  img: "https://qualityroofsandlandscapes.co.uk/wp-content/uploads/2026/05/new-roof.jpg?v=8qmpfco9j2v",
  alt: "Professional roofing services and installations in Southampton",
  description: "Complete roofing solutions, pitched roof repairs, tile replacement and inspections."
}, {
  title: "Fascias & Soffits",
  to: "/FasciasSoffitsPage",
  img: "https://qualityroofsandlandscapes.co.uk/wp-content/uploads/2026/05/new-facias-soffits-gutters.jpg?v=0io3fig2tp",
  alt: "Clean white uPVC fascias and ventilated soffits fitted on a Southampton property",
  description: "Low-maintenance uPVC fascias, ventilated soffits and bargeboards protecting timber rooflines."
}, {
  title: "Guttering",
  to: "/GutteringPage",
  img: "https://qualityroofsandlandscapes.co.uk/wp-content/uploads/2026/10/roofer-replacing-guttering-southampton.jpg",
  alt: "Roofer fitting new high-capacity guttering on a property in Southampton",
  description: "High-capacity rainwater systems, downpipes, gutter repairs and clearing for South Coast weather."
}, {
  title: "Flat Roofs",
  to: "/FlatRoofsPage",
  img: "https://qualityroofsandlandscapes.co.uk/wp-content/uploads/2026/05/flat-roof.webp?v=t143rwcbvdo",
  alt: "Modern flat roofing installation in Southampton",
  description: "Durable high-performance GRP fibreglass and EPDM rubber flat roofs."
}, {
  title: "New Roofs",
  to: "/NewRoofsPage",
  img: "https://qualityroofsandlandscapes.co.uk/wp-content/uploads/2026/05/new-roof-work.jpg?v=mt34xrimrx",
  alt: "Complete new roof construction project in Southampton",
  description: "Full strip-out and brand new pitched tiled and slate roof installations."
}, {
  title: "Chimneys",
  to: "/ChimneysPage",
  img: "https://qualityroofsandlandscapes.co.uk/wp-content/uploads/2026/05/chimney-job.jpg?v=nfxhvm4bff",
  alt: "Professional chimney repair and maintenance in Southampton",
  description: "Chimney repairs, lead flashing, structural repointing, and cowls."
}, {
  title: "Lead Work",
  to: "/LeadWorkPage",
  img: "https://qualityroofsandlandscapes.co.uk/wp-content/uploads/2026/05/chimney-job.jpg?v=nfxhvm4bff",
  alt: "Precision lead work and flashings fitted on a Southampton roof",
  description: "Milled lead flashings, valleys, aprons, soakers, and bespoke lead work installations."
}, {
  title: "Repointing",
  to: "/RePointingServicesPage",
  img: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1662274/img/modern_brickwork_repointing.png",
  alt: "Professional modern brickwork repointing and mortar repair in Southampton",
  description: "Weatherproof mortar repair, brickwork restoration and ridge tile repointing."
}, {
  title: "Roof Cleaning",
  to: "/RoofCleaningPage",
  img: "https://qualityroofsandlandscapes.co.uk/wp-content/uploads/2026/05/roofing-before-and-after-cleaning.jpg?v=iubj9xvr7t",
  alt: "Roof cleaning and moss removal specialist in Southampton",
  description: "Safe moss removal, tile scraping, and biocide protective treatments."
}];
export default function ServicesGrid() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section id="servicesgrid" className="bg-[#F7F5F1] py-20 md:py-28 overflow-hidden" ref={ref}>
      <div className={`max-w-[1280px] mx-auto px-6 lg:px-12 transition-all duration-1000 ease-out motion-reduce:transition-none motion-reduce:transform-none ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
        <div className="text-center max-w-2xl mx-auto mb-12 md:mb-16">
          <p className="text-xs md:text-sm font-bold uppercase tracking-widest text-primary mb-3">
            WHAT WE DO
          </p>
          <h2 className="font-default text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight text-secondary-foreground mb-4">
            Our Roofing Services in Southampton
          </h2>
          <p className="text-base md:text-lg text-foreground/80 leading-relaxed">
            From complete pitched roof installations and flat roofing systems to chimney repointing and guttering across Southampton, every job is handled by Matt and our skilled team.
          </p>
        </div>

        {/* 9 roofing services displayed in a balanced 3-column responsive grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {roofingServices.map((service, index) => {
          return <div data-index={index} key={service.to} className="h-full">
                <Link to={service.to} className="group block h-full rounded-[14px] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2">
                  <div className="relative aspect-[4/3.5] overflow-hidden rounded-[14px] bg-muted shadow-[0_4px_20px_-2px_rgba(21,25,29,0.06)] transition-all duration-300 motion-reduce:transition-none group-hover:-translate-y-1.5 motion-reduce:group-hover:translate-y-0 group-hover:shadow-[0_12px_30px_-4px_rgba(21,25,29,0.16)]">
                    <img src={service.img} alt={service.alt} loading="lazy" className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 ease-out motion-reduce:transition-none group-hover:scale-[1.06] motion-reduce:group-hover:scale-100" />

                    {/* Gradient overlay for readability */}
                    <div className="absolute inset-0 bg-gradient-to-t from-[#15191D]/90 via-[#15191D]/40 to-transparent pointer-events-none" />

                    {/* Bottom content row: service name + description + arrow circle */}
                    <div className="absolute inset-x-0 bottom-0 p-5 md:p-6 flex items-end justify-between gap-4 z-10 pointer-events-none">
                      <div>
                        <h3 className="font-default font-bold text-white text-lg md:text-xl leading-snug">
                          {service.title}
                        </h3>
                        <p className="font-default text-xs text-white/80 line-clamp-2 mt-1">
                          {service.description}
                        </p>
                      </div>
                      <div aria-hidden="true" className="shrink-0 w-10 h-10 rounded-full bg-primary flex items-center justify-center text-primary-foreground shadow-sm transition-transform duration-200 motion-reduce:transition-none group-hover:scale-110">
                        <ArrowRight className="w-5 h-5 transition-transform duration-200 motion-reduce:transition-none group-hover:translate-x-0.5 motion-reduce:group-hover:translate-x-0" />
                      </div>
                    </div>
                  </div>
                </Link>
              </div>;
        })}
        </div>
      </div>
    </section>;
}